/**
 * Marketplace Chat Page - Individual conversation view
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, Loader2, Package } from 'lucide-react';
import { api } from '../../../services/api';
import { ChatMessage, ConditionBadge } from '../../../components/marketplace';
import { useAuthStore } from '../../../stores/auth-store';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function Chat() {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [newMessage, setNewMessage] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['marketplace-conversation', id],
    queryFn: () => api.getMarketplaceConversation(id!, { limit: 100 }),
    enabled: !!id,
    refetchInterval: 10000, // Poll every 10 seconds
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => api.sendMarketplaceMessage(id!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketplace-conversation', id] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-conversations'] });
      setNewMessage('');
    },
  });

  // Mark as read on load
  useEffect(() => {
    if (id) {
      api.markMarketplaceConversationRead(id).catch(() => {
        // Silently ignore errors
      });
    }
  }, [id]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.data?.messages]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sendMutation.isPending) return;
    sendMutation.mutate(newMessage.trim());
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">{t('chat.loadError', 'Failed to load conversation')}</p>
        <button
          onClick={() => navigate('/account/marketplace/messages')}
          className="text-primary-600 hover:text-primary-700"
        >
          {t('chat.backToMessages', 'Back to Messages')}
        </button>
      </div>
    );
  }

  const { conversation, listing, other_party, messages } = data.data;

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 p-4 rounded-t-xl">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/account/marketplace/messages')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Listing Preview */}
          <Link
            to={`/marketplace/listing/${listing.id}`}
            className="flex items-center gap-3 flex-1 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
          >
            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              {listing.photos?.[0] ? (
                <img
                  src={listing.photos[0]}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {listing.product_code}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                  {formatPrice(listing.asking_price)}
                </span>
                <ConditionBadge condition={listing.condition} />
              </div>
            </div>
          </Link>

          {/* Other Party */}
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              {other_party.email?.split('@')[0]}
            </p>
            <p className="text-xs text-gray-500">
              {conversation.is_buyer ? t('chat.seller', 'Seller') : t('chat.buyer', 'Buyer')}
            </p>
          </div>
        </div>

        {/* Listing Status Banner */}
        {listing.status !== 'active' && (
          <div className="mt-3 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
            {t('chat.listingUnavailable', 'This listing is no longer available ({status})').replace('{status}', listing.status)}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-gray-50">
        {/* Date Header */}
        {messages.length > 0 && (
          <div className="flex justify-center mb-4">
            <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
              {new Date(messages[0].created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            content={msg.content}
            messageType={msg.message_type}
            isOwnMessage={msg.sender_id === user?.id}
            readAt={msg.read_at}
            createdAt={msg.created_at}
            senderEmail={msg.sender_id !== user?.id ? other_party.email : undefined}
          />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4 rounded-b-xl">
        <form onSubmit={handleSend} className="flex items-center gap-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('chat.placeholder', 'Type a message...')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={listing.status !== 'active'}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sendMutation.isPending || listing.status !== 'active'}
            className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sendMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>

        {sendMutation.error && (
          <p className="mt-2 text-sm text-red-600 text-center">
            {t('chat.sendError', 'Failed to send message. Please try again.')}
          </p>
        )}
      </div>
    </div>
  );
}
